import React, { useEffect, useState } from 'react';
import { CountrySettings, fetchCountrySettings, updateCountrySettings, addCountrySettings, deleteCountrySettings } from '../../services/countryService';

const CountrySettingsComponent: React.FC = () => {
  const [countries, setCountries] = useState<CountrySettings[]>([]);
  const [newCountryCode, setNewCountryCode] = useState('');
  const [newCountryName, setNewCountryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await fetchCountrySettings();
      setCountries(data);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading countries');
      setLoading(false);
    }
  };

  const handleToggleCountry = async (countryCode: string, currentStatus: boolean) => {
    try {
      await updateCountrySettings(countryCode, !currentStatus);
      await loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating country');
    }
  };

  const handleAddCountry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCountryCode || !newCountryName) {
      setError('Please fill in both country code and name');
      return;
    }

    try {
      await addCountrySettings(newCountryCode.toUpperCase(), newCountryName);
      setNewCountryCode('');
      setNewCountryName('');
      await loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding country');
    }
  };

  const handleDeleteCountry = async (countryCode: string) => {
    if (countryCode === 'NZ') {
      setError('Cannot delete default country (NZ)');
      return;
    }

    try {
      await deleteCountrySettings(countryCode);
      await loadCountries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting country');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="country-settings">
      <h2>Country Settings</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="card mb-4">
        <div className="card-body">
          <h3 className="card-title h5">Add New Country</h3>
          <form onSubmit={handleAddCountry} className="row g-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Country Code (e.g., AU)"
                value={newCountryCode}
                onChange={(e) => setNewCountryCode(e.target.value)}
                maxLength={2}
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Country Name"
                value={newCountryName}
                onChange={(e) => setNewCountryName(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <button type="submit" className="btn btn-primary w-100">Add Country</button>
            </div>
          </form>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((country) => (
              <tr key={country.id}>
                <td>{country.country_code}</td>
                <td>{country.country_name}</td>
                <td>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={country.is_active}
                      onChange={() => handleToggleCountry(country.country_code, country.is_active)}
                      id={`country-${country.country_code}`}
                    />
                    <label className="form-check-label" htmlFor={`country-${country.country_code}`}>
                      {country.is_active ? 'Active' : 'Inactive'}
                    </label>
                  </div>
                </td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteCountry(country.country_code)}
                    disabled={country.country_code === 'NZ'}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CountrySettingsComponent;
